import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./PrintExamSheet.css";
import {
  FaPrint,
  FaFilePdf,
  FaImage,
  FaEdit,
  FaTimes,
  FaArrowLeft,
  FaCompress,
  FaExpand,
  FaUserGraduate,
  FaCalculator,
  FaCalendarAlt,
  FaBook,
  FaGraduationCap,
  FaUniversity,
  FaSignature,
} from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PrintExamSheet = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);

  // Raw data from navigation state
  const studentsRaw = location.state?.students || [];
  const examDetailsRaw = location.state?.examDetails || {};

  // Map students: ensure they have all fields that might be needed
  const students = studentsRaw.map((s) => ({
    id: s.id || s.student_id || "N/A",
    name: s.name || s.student_name || "N/A",
    father_name: s.father_name || "N/A",
    class: s.class || "N/A",
    section: s.section || "N/A",
    subject: s.subject || "N/A",
    totalMarks: s.totalMarks || "0",
    obtainedMarks: s.obtainedMarks || "",
    percentage: s.percentage || 0,
    status: s.status || "pending",
    mobile: s.mobile || "",
    b_form_no: s.b_form_no || "",
    address: s.address || "",
    gender: s.gender || "",
    family_status: s.family_status || "",
    // Add missing fields that are used for fallback
    examType: s.examType || "N/A",
    date: s.date || null,
    day: s.day || "N/A",
  }));

  // Get first student to use as fallback for missing exam details
  const firstStudent = students.length > 0 ? students[0] : null;

  // Enhanced exam details – use raw values if present, otherwise fallback to first student's data
  const examDetails = {
    class: examDetailsRaw.className || examDetailsRaw.class || firstStudent?.class || "N/A",
    class_section: examDetailsRaw.classSection || examDetailsRaw.class_section || firstStudent?.section || "N/A",
    subject: examDetailsRaw.subject || firstStudent?.subject || "N/A",
    exam_date: examDetailsRaw.date || examDetailsRaw.exam_date || firstStudent?.date || new Date().toISOString(),
    exam_type: examDetailsRaw.examType || examDetailsRaw.exam_type || firstStudent?.examType || "N/A",
    examId: examDetailsRaw.examId || location.state?.examId || "N/A",
    total_marks: examDetailsRaw.totalMarks || examDetailsRaw.total_marks || firstStudent?.totalMarks || "0",
    examDay: examDetailsRaw.examDay || examDetailsRaw.exam_day || firstStudent?.day || "N/A",
    examTypeOption: examDetailsRaw.examTypeOption || "predefined",
  };

  const examId = examDetails.examId;

  // Customization state
  const [customSettings, setCustomSettings] = useState({
    watermark: "OFFICIAL EXAM RECORD",
    header: `${examDetails.exam_type} Mark Entry Sheet`,
    logo: null,
    showSignatures: true,
    showSchoolHeader: true,
    fontSize: "medium",
    theme: "professional",
  });

  const [isCustomizing, setIsCustomizing] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [pageScale, setPageScale] = useState(1);
  const [selectedColumns, setSelectedColumns] = useState({
    serial: true,
    studentId: true,
    studentName: true,
    class: true,
    section: true,
    subject: true,
    totalMarks: true,
    obtainedMarks: true,
  });

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCustomSettings((prev) => ({ ...prev, logo: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper to load images
  const loadImage = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = url;
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // ==================== ENHANCED PDF GENERATION ====================
  const downloadPDF = async () => {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
      hotfixes: ["px_scaling"],
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;
    let yPos = 5; // Start closer to top

    // Load left and right logos from public folder
    const logoLeft = await loadImage("/images/l1.jpeg");
    const logoRight = await loadImage("/images/l2.png");
    const uploadedLogo = customSettings.logo ? await loadImage(customSettings.logo) : null;

    // Professional color palette
    const primaryColor = [44, 50, 146];   // #2C3292
    const secondaryColor = [178, 148, 94]; // Gold
    const lightBg = [248, 249, 250];
    const textColor = [44, 62, 80];

    // Decorative bubbles
    pdf.setGState(new pdf.GState({ opacity: 0.03 }));
    pdf.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    pdf.circle(pageWidth - 60, -30, 100, "F");
    pdf.circle(-40, pageHeight - 80, 80, "F");
    pdf.circle(pageWidth / 2 + 50, pageHeight / 2 - 50, 50, "F");
    pdf.setGState(new pdf.GState({ opacity: 1.0 }));

    // Watermark
    if (customSettings.watermark) {
      pdf.setGState(new pdf.GState({ opacity: 0.02 }));
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(60);
      pdf.setFont("helvetica", "bold");
      pdf.text(customSettings.watermark, pageWidth / 2, pageHeight / 2, {
        align: "center",
        angle: 45,
      });
      pdf.setGState(new pdf.GState({ opacity: 1.0 }));
    }

    // School Header
    if (customSettings.showSchoolHeader) {
      // Left logo
      if (logoLeft) {
        pdf.addImage(logoLeft, "JPEG", margin, yPos, 20, 20);
      }

      // Right logo
      if (logoRight) {
        pdf.addImage(logoRight, "PNG", pageWidth - margin - 20, yPos, 20, 20);
      }

      // School Name
      pdf.setFontSize(22);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      pdf.text("DAR-E-ARQAM", pageWidth / 2, yPos + 10, { align: "center" });

      yPos += 15;

      // Campus Name
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "italic");
      pdf.setTextColor(100, 100, 100);
      pdf.text("KOTLI LOHARAN EAST CAMPUS", pageWidth / 2, yPos, { align: "center" });

      yPos += 8;

      // Project Bar
      pdf.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      pdf.rect(margin, yPos, contentWidth, 8, "F");
      pdf.setTextColor(255);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.text("Project of Punjab Group of Colleges", pageWidth / 2, yPos + 5.5, { align: "center" });

      yPos += 12;

      // Uploaded Logo (if no left/right logos)
      if (uploadedLogo && !logoLeft && !logoRight) {
        pdf.addImage(uploadedLogo, "PNG", pageWidth / 2 - 10, yPos - 10, 20, 20);
      }

      // Decorative line
      pdf.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;
    }

    // Document Badge
    pdf.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    pdf.roundedRect(margin, yPos, contentWidth, 10, 3, 3, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text(customSettings.header, pageWidth / 2, yPos + 7, { align: "center" });
    yPos += 20;

    // Exam Details Grid
    const examDetailsData = [
      { label: "Class:", value: examDetails.class },
      { label: "Section:", value: examDetails.class_section },
      { label: "Subject:", value: examDetails.subject },
      { label: "Exam Date:", value: formatDate(examDetails.exam_date) },
      { label: "Exam Type:", value: examDetails.exam_type },
      { label: "Exam ID:", value: examId },
    ];

    pdf.setFontSize(9);
    pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
    const colWidth = contentWidth / 2;
    examDetailsData.forEach((detail, index) => {
      const colIndex = index % 2;
      const rowIndex = Math.floor(index / 2);
      const x = margin + colIndex * colWidth;
      const y = yPos + rowIndex * 6;

      pdf.setFont("helvetica", "bold");
      pdf.text(detail.label, x, y);
      const labelWidth = pdf.getTextWidth(detail.label);
      pdf.setFont("helvetica", "normal");
      pdf.text(detail.value, x + labelWidth + 2, y);
    });

    yPos += Math.ceil(examDetailsData.length / 2) * 6 + 10;

    // Student Marks Table
    const headers = [];
    const rows = [];

    if (selectedColumns.serial) headers.push("S.No");
    if (selectedColumns.studentId) headers.push("Student ID");
    if (selectedColumns.studentName) headers.push("Student Name");
    if (selectedColumns.class) headers.push("Class");
    if (selectedColumns.section) headers.push("Section");
    if (selectedColumns.subject) headers.push("Subject");
    if (selectedColumns.totalMarks) headers.push("Total Marks");
    if (selectedColumns.obtainedMarks) headers.push("Obtained Marks");

    students.forEach((student, index) => {
      const row = [];
      if (selectedColumns.serial) row.push((index + 1).toString());
      if (selectedColumns.studentId) row.push(student.id);
      if (selectedColumns.studentName) row.push(student.name);
      if (selectedColumns.class) row.push(student.class);
      if (selectedColumns.section) row.push(student.section);
      if (selectedColumns.subject) row.push(student.subject);
      if (selectedColumns.totalMarks) row.push(student.totalMarks);
      if (selectedColumns.obtainedMarks) row.push(student.obtainedMarks || "");
      rows.push(row);
    });

    autoTable(pdf, {
      startY: yPos,
      head: [headers],
      body: rows,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        textColor: textColor,
      },
      headStyles: {
        fillColor: primaryColor,
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
      },
      alternateRowStyles: {
        fillColor: lightBg,
      },
      columnStyles: {
        0: { cellWidth: "auto", halign: "center" }, // S.No
        1: { cellWidth: "auto", halign: "center" }, // Student ID
        2: { cellWidth: 35 }, // Student Name
        3: { cellWidth: "auto", halign: "center" }, // Class
        4: { cellWidth: "auto", halign: "center" }, // Section
        5: { cellWidth: 20, halign: "center" }, // Subject
        6: { cellWidth: "auto", halign: "center" }, // Total Marks
        7: { cellWidth: "auto", halign: "center", fillColor: [255, 255, 204] }, // Obtained Marks
      },
      didDrawPage: (data) => {
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        const pageCount = pdf.getNumberOfPages();
        pdf.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      },
    });

    yPos = pdf.lastAutoTable.finalY + 10;

    // Footer Details
    pdf.setFontSize(8);
    pdf.setTextColor(textColor[0], textColor[1], textColor[2]);

    const footerDetails = [
      {
        label: "Generated On:",
        value: new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
      { label: "System ID:", value: examId },
      { label: "Printed By:", value: "Exam Management System" },
      { label: "Total Pages:", value: pdf.getNumberOfPages().toString() },
    ];

    const footerColWidth = contentWidth / 4;
    footerDetails.forEach((detail, index) => {
      const x = margin + index * footerColWidth;
      pdf.setFont("helvetica", "bold");
      pdf.text(detail.label, x, yPos);
      pdf.setFont("helvetica", "normal");
      pdf.text(detail.value, x, yPos + 4);
    });

    yPos += 15;

    // Signatures
    if (customSettings.showSignatures && yPos < pageHeight - 40) {
      const signatureColWidth = contentWidth / 3;
      const signatures = [
        { title: "Marks Entered By:", role: "Subject Teacher" },
        { title: "Verified By:", role: "Head of Department" },
        { title: "Approved By:", role: "Principal" },
      ];

      signatures.forEach((sig, index) => {
        const x = margin + index * signatureColWidth;
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.line(x, yPos, x + signatureColWidth - 10, yPos);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text(sig.title, x, yPos + 5);
        pdf.setFont("helvetica", "bold");
        pdf.text(sig.role, x, yPos + 10);
      });

      yPos += 25;
    }

    // Instructions
    if (yPos < pageHeight - 20) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.text("Instructions for Mark Entry:", margin, yPos);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      const instructions = [
        "1. Enter marks in the 'Obtained Marks' column using black or blue ink.",
        "2. Write clearly and legibly.",
        "3. Round off marks to the nearest whole number if required.",
        "4. Submit this sheet to the examination office within 24 hours of the exam.",
      ];

      instructions.forEach((inst, idx) => {
        pdf.text(inst, margin, yPos + 5 + idx * 3.5);
      });
    }

    // Save PDF
    const fileName = `MarkEntrySheet-${examId}-${
      new Date().toISOString().split("T")[0]
    }.pdf`;
    pdf.save(fileName);
  };

  // Handle print
  const handlePrint = () => {
    const originalScale = pageScale;
    setPageScale(1);

    setTimeout(() => {
      window.print();
      setTimeout(() => setPageScale(originalScale), 500);
    }, 100);
  };

  // Handle full screen toggle
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    if (!isFullScreen) {
      setPageScale(1);
    }
  };

  // Handle page scaling
  const handleZoomIn = () => {
    setPageScale((prev) => Math.min(prev + 0.1, 1.5));
  };

  const handleZoomOut = () => {
    setPageScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  // Get font size class
  const getFontSizeClass = () => {
    switch (customSettings.fontSize) {
      case "small":
        return "exam-print-font-small";
      case "large":
        return "exam-print-font-large";
      default:
        return "exam-print-font-medium";
    }
  };

  // Get theme class
  const getThemeClass = () => {
    switch (customSettings.theme) {
      case "academic":
        return "exam-print-theme-academic";
      case "corporate":
        return "exam-print-theme-corporate";
      default:
        return "exam-print-theme-professional";
    }
  };

  // Toggle column visibility
  const toggleColumn = (column) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // Reset zoom on window resize
  useEffect(() => {
    const handleResize = () => {
      if (!isFullScreen) {
        setPageScale(1);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isFullScreen]);

  // Update header when exam type changes (if raw data changes)
  useEffect(() => {
    setCustomSettings((prev) => ({
      ...prev,
      header: `${examDetails.exam_type} Mark Entry Sheet`,
    }));
  }, [examDetails.exam_type]);

  return (
    <div
      className={`exam-print-page ${
        isFullScreen ? "exam-print-full-screen" : ""
      }`}
    >
      {/* Header */}
      <div className="exam-print-header">
        <button className="exam-print-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back to Records
        </button>
        <h2>
          <FaPrint /> Exam Mark Entry Sheet - A4 Format
        </h2>
        <div className="exam-print-header-actions">
          <button
            className="exam-print-customize-btn"
            onClick={() => setIsCustomizing(!isCustomizing)}
          >
            <FaEdit /> {isCustomizing ? "Hide Options" : "Customize"}
          </button>

          <div className="exam-print-zoom-controls">
            <button
              className="exam-print-zoom-btn"
              onClick={handleZoomOut}
              title="Zoom Out"
            >
              -
            </button>
            <span className="exam-print-zoom-level">
              {Math.round(pageScale * 100)}%
            </span>
            <button
              className="exam-print-zoom-btn"
              onClick={handleZoomIn}
              title="Zoom In"
            >
              +
            </button>
          </div>

          <button
            className="exam-print-fullscreen-btn"
            onClick={toggleFullScreen}
          >
            {isFullScreen ? <FaCompress /> : <FaExpand />}
            {isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
          </button>

          <button className="exam-print-pdf-btn" onClick={downloadPDF}>
            <FaFilePdf /> Download PDF
          </button>
          <button className="exam-print-print-btn" onClick={handlePrint}>
            <FaPrint /> Print
          </button>
        </div>
      </div>

      <div className="exam-print-container">
        {/* Customization Panel */}
        {isCustomizing && (
          <div className="exam-print-customization-panel">
            <div className="exam-print-panel-header">
              <h3>Customize Mark Sheet</h3>
              <button
                className="exam-print-close-panel"
                onClick={() => setIsCustomizing(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="exam-print-customization-group">
              <label>
                <FaBook /> Document Title
              </label>
              <input
                type="text"
                value={customSettings.header}
                onChange={(e) =>
                  setCustomSettings((prev) => ({
                    ...prev,
                    header: e.target.value,
                  }))
                }
                placeholder="Enter document title"
              />
            </div>

            <div className="exam-print-customization-group">
              <label>Watermark Text</label>
              <input
                type="text"
                value={customSettings.watermark}
                onChange={(e) =>
                  setCustomSettings((prev) => ({
                    ...prev,
                    watermark: e.target.value,
                  }))
                }
                placeholder="Enter watermark text"
              />
            </div>

            <div className="exam-print-customization-group">
              <label>Theme</label>
              <div className="exam-print-theme-buttons">
                <button
                  className={
                    customSettings.theme === "professional" ? "active" : ""
                  }
                  onClick={() =>
                    setCustomSettings((prev) => ({
                      ...prev,
                      theme: "professional",
                    }))
                  }
                >
                  Professional
                </button>
                <button
                  className={
                    customSettings.theme === "academic" ? "active" : ""
                  }
                  onClick={() =>
                    setCustomSettings((prev) => ({
                      ...prev,
                      theme: "academic",
                    }))
                  }
                >
                  Academic
                </button>
                <button
                  className={
                    customSettings.theme === "corporate" ? "active" : ""
                  }
                  onClick={() =>
                    setCustomSettings((prev) => ({
                      ...prev,
                      theme: "corporate",
                    }))
                  }
                >
                  Corporate
                </button>
              </div>
            </div>

            <div className="exam-print-customization-group">
              <label>Font Size</label>
              <div className="exam-print-font-size-buttons">
                <button
                  className={
                    customSettings.fontSize === "small" ? "active" : ""
                  }
                  onClick={() =>
                    setCustomSettings((prev) => ({
                      ...prev,
                      fontSize: "small",
                    }))
                  }
                >
                  Small
                </button>
                <button
                  className={
                    customSettings.fontSize === "medium" ? "active" : ""
                  }
                  onClick={() =>
                    setCustomSettings((prev) => ({
                      ...prev,
                      fontSize: "medium",
                    }))
                  }
                >
                  Medium
                </button>
                <button
                  className={
                    customSettings.fontSize === "large" ? "active" : ""
                  }
                  onClick={() =>
                    setCustomSettings((prev) => ({
                      ...prev,
                      fontSize: "large",
                    }))
                  }
                >
                  Large
                </button>
              </div>
            </div>

            <div className="exam-print-customization-group">
              <label>
                <FaImage /> School Logo
              </label>
              <div className="exam-print-logo-upload">
                <label className="exam-print-upload-btn">
                  <FaImage /> Upload Logo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    hidden
                  />
                </label>
                {customSettings.logo && (
                  <div className="exam-print-logo-preview">
                    <img src={customSettings.logo} alt="Logo" />
                    <button
                      onClick={() =>
                        setCustomSettings((prev) => ({ ...prev, logo: null }))
                      }
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="exam-print-customization-group">
              <label>Display Options</label>
              <div className="exam-print-checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={customSettings.showSchoolHeader}
                    onChange={(e) =>
                      setCustomSettings((prev) => ({
                        ...prev,
                        showSchoolHeader: e.target.checked,
                      }))
                    }
                  />
                  Show School Header
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={customSettings.showSignatures}
                    onChange={(e) =>
                      setCustomSettings((prev) => ({
                        ...prev,
                        showSignatures: e.target.checked,
                      }))
                    }
                  />
                  <FaSignature /> Show Signatures
                </label>
              </div>
            </div>

            <div className="exam-print-customization-group">
              <label>
                <FaUserGraduate /> Table Columns
              </label>
              <div className="exam-print-column-checkboxes">
                {Object.entries(selectedColumns).map(([column, isSelected]) => (
                  <label key={column}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleColumn(column)}
                    />
                    {column === "serial" && "Serial No."}
                    {column === "studentId" && "Student ID"}
                    {column === "studentName" && "Student Name"}
                    {column === "class" && "Class"}
                    {column === "section" && "Section"}
                    {column === "subject" && "Subject"}
                    {column === "totalMarks" && "Total Marks"}
                    {column === "obtainedMarks" && "Obtained Marks"}
                  </label>
                ))}
              </div>
            </div>

            <div className="exam-print-instructions">
              <p>
                <strong>Mark Entry Instructions:</strong>
              </p>
              <ul>
                <li>Enter marks in the "Obtained Marks" column</li>
                <li>Use black or blue ink for manual entry</li>
                <li>Write clearly and legibly</li>
                <li>Keep decimal values to two places if needed</li>
              </ul>
            </div>
          </div>
        )}

        {/* Main Preview */}
        <div
          className={`exam-print-preview-wrapper ${
            isFullScreen ? "exam-print-full-screen-mode" : ""
          }`}
        >
          <div
            className={`exam-print-a4-paper ${getFontSizeClass()} ${getThemeClass()}`}
            ref={printRef}
            style={{
              transform: `scale(${pageScale})`,
              transformOrigin: "center top",
            }}
          >
            {/* Watermark */}
            {customSettings.watermark && (
              <div className="exam-print-watermark-overlay">
                {customSettings.watermark}
              </div>
            )}

            {/* School Header (CSS version matches PDF style) */}
            {customSettings.showSchoolHeader && (
              <div className="exam-print-school-header">
                {customSettings.logo && (
                  <div className="exam-print-logo">
                    <img src={customSettings.logo} alt="School Logo" />
                  </div>
                )}
                <div className="exam-print-school-details">
                  <h1 style={{ color: "#2C3292" }}>
                    <FaUniversity /> DAR-E-ARQAM
                  </h1>
                  <p className="exam-print-school-motto">
                    KOTLI LOHARAN EAST CAMPUS
                  </p>
                  <p className="exam-print-school-address">
                    Project of Punjab Group of Colleges
                  </p>
                </div>
              </div>
            )}

            {/* Exam Header */}
            <div className="exam-print-exam-header">
              <h2>
                <FaCalculator /> {customSettings.header}
              </h2>
              <div className="exam-print-exam-details">
                <div className="exam-print-detail-grid">
                  <div className="exam-print-detail-item">
                    <span className="exam-print-detail-label">
                      <FaGraduationCap /> Class:
                    </span>
                    <span className="exam-print-detail-value">
                      {examDetails.class}
                    </span>
                  </div>
                  <div className="exam-print-detail-item">
                    <span className="exam-print-detail-label">Section:</span>
                    <span className="exam-print-detail-value">
                      {examDetails.class_section}
                    </span>
                  </div>
                  <div className="exam-print-detail-item">
                    <span className="exam-print-detail-label">
                      <FaBook /> Subject:
                    </span>
                    <span className="exam-print-detail-value">
                      {examDetails.subject}
                    </span>
                  </div>
                  <div className="exam-print-detail-item">
                    <span className="exam-print-detail-label">
                      <FaCalendarAlt /> Exam Date:
                    </span>
                    <span className="exam-print-detail-value">
                      {formatDate(examDetails.exam_date)}
                    </span>
                  </div>
                  <div className="exam-print-detail-item">
                    <span className="exam-print-detail-label">Exam Type:</span>
                    <span className="exam-print-detail-value">
                      {examDetails.exam_type}
                    </span>
                  </div>
                  <div className="exam-print-detail-item">
                    <span className="exam-print-detail-label">Exam ID:</span>
                    <span className="exam-print-detail-value exam-print-exam-id">
                      {examId}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="exam-print-table-container">
              <table className="exam-print-marks-table">
                <thead>
                  <tr>
                    {selectedColumns.serial && (
                      <th className="exam-print-col-serial">#</th>
                    )}
                    {selectedColumns.studentId && (
                      <th className="exam-print-col-id">Student ID</th>
                    )}
                    {selectedColumns.studentName && (
                      <th className="exam-print-col-name">Student Name</th>
                    )}
                    {selectedColumns.class && (
                      <th className="exam-print-col-class">Class</th>
                    )}
                    {selectedColumns.section && (
                      <th className="exam-print-col-section">Section</th>
                    )}
                    {selectedColumns.subject && (
                      <th className="exam-print-col-subject">Subject</th>
                    )}
                    {selectedColumns.totalMarks && (
                      <th className="exam-print-col-total">Total Marks</th>
                    )}
                    {selectedColumns.obtainedMarks && (
                      <th className="exam-print-col-obtained">
                        Obtained Marks
                        <div className="exam-print-column-note">
                          (To be filled by teacher)
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => (
                    <tr key={student.id} className="exam-print-student-row">
                      {selectedColumns.serial && (
                        <td className="exam-print-col-serial">{index + 1}</td>
                      )}
                      {selectedColumns.studentId && (
                        <td className="exam-print-col-id">
                          <div className="exam-print-student-id">
                            <FaUserGraduate /> {student.id}
                          </div>
                        </td>
                      )}
                      {selectedColumns.studentName && (
                        <td className="exam-print-col-name exam-print-text-wrap">
                          {student.name}
                        </td>
                      )}
                      {selectedColumns.class && (
                        <td className="exam-print-col-class">
                          {student.class}
                        </td>
                      )}
                      {selectedColumns.section && (
                        <td className="exam-print-col-section">
                          {student.section}
                        </td>
                      )}
                      {selectedColumns.subject && (
                        <td className="exam-print-col-subject">
                          {student.subject}
                        </td>
                      )}
                      {selectedColumns.totalMarks && (
                        <td className="exam-print-col-total">
                          <div className="exam-print-total-marks">
                            {student.totalMarks}
                          </div>
                        </td>
                      )}
                      {selectedColumns.obtainedMarks && (
                        <td className="exam-print-col-obtained">
                          <div className="exam-print-marks-entry-box">
                            {parseFloat(student.obtainedMarks) > 0
                              ? student.obtainedMarks
                              : ""}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="exam-print-footer">
              <div className="exam-print-footer-details">
                <div className="exam-print-footer-grid">
                  <div className="exam-print-footer-item">
                    <strong>Generated On:</strong>
                    <span>
                      {new Date().toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <div className="exam-print-footer-item">
                    <strong>System ID:</strong>
                    <span>{examId}</span>
                  </div>
                  <div className="exam-print-footer-item">
                    <strong>Printed By:</strong>
                    <span>Exam Management System</span>
                  </div>
                  <div className="exam-print-footer-item">
                    <strong>Total Pages:</strong>
                    <span>1</span>
                  </div>
                </div>
              </div>

              {/* Signatures */}
              {customSettings.showSignatures && (
                <div className="exam-print-signatures">
                  <div className="exam-print-signature">
                    <div className="exam-print-signature-line"></div>
                    <p>Marks Entered By:</p>
                    <p className="exam-print-signature-title">
                      Subject Teacher
                    </p>
                  </div>
                  <div className="exam-print-signature">
                    <div className="exam-print-signature-line"></div>
                    <p>Verified By:</p>
                    <p className="exam-print-signature-title">
                      Head of Department
                    </p>
                  </div>
                  <div className="exam-print-signature">
                    <div className="exam-print-signature-line"></div>
                    <p>Approved By:</p>
                    <p className="exam-print-signature-title">Principal</p>
                  </div>
                </div>
              )}

              <div className="exam-print-disclaimer">
                <p>
                  <strong>Instructions for Mark Entry:</strong>
                  1. Enter marks in the "Obtained Marks" column using black or
                  blue ink. <br />
                  2. Write clearly and legibly. <br />
                  3. Round off marks to the nearest whole number if required.{" "}
                  <br />
                  4. Submit this sheet to the examination office within 24 hours
                  of the exam.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintExamSheet;